import useSWR from "swr";
import { Tab } from ".";
import { customFetcher, getTsDate, notify } from "../../../utils";
import { ActivationCodes, AppSettings, DeviceActivations } from "../../../globals/interfaces";
import VirtualTable from "../../../components/custom/VirtualTable";
import useModal from "../../../customHooks/useModal";
import { useEffect, useState } from "react";
import apiClient from "../../../apiClient";
import useUser from "../../../customHooks/useUser";
import ModalUpdate from "./ModalUpdate";
import { Button } from "reactstrap";
import { io, Socket } from "socket.io-client";

const DISPLAY_ACTIVATION_URL = 'https://display-activation-fpc5ggc3evhegwhn.eastus-01.azurewebsites.net';

export default function Active({
    activeTab
}: {
    activeTab: Tab;
}) {

    const { user } = useUser();
    const key = activeTab === 'active' ? '/display-activation/active' : null;
    const {
        data,
        mutate
    } = useSWR(key, customFetcher<DeviceActivations>, {
        refreshInterval: 5000,
        fallbackData: []
    });

    const { modal, setModal } = useModal();
    const [device_id, set_device_id] = useState<string>();
    const selectedItem = data.find(item => item.device_id === device_id);

    // Socket connection for real-time status updates
    useEffect(() => {
        if (activeTab !== 'active') return;

        let socket: Socket | null = null;

        socket = io(DISPLAY_ACTIVATION_URL, {
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 3000,
        });

        socket.on('connect', () => {
            console.log('✅ Admin socket connected');
            socket?.emit('ADMIN_JOIN');
        });

        socket.on('DEVICE_STATUS_CHANGED', () => {
            mutate();
        });

        socket.on('disconnect', (reason) => {
            console.log('🔌 Admin socket disconnected:', reason);
        });

        socket.on('connect_error', (err) => {
            console.log('🔴 Admin socket error:', err.message);
        });

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [activeTab, mutate]);

    const handleSelectItem = (device_id: string) => {
        set_device_id(device_id);
        setModal(true);
    }

    const updateSettings = async (settings: AppSettings) => {
        try {
            await apiClient.put(`/display-activation/update`, {
                deviceId: device_id,
                userId: user.s_email,
                settings,
                updatedAt: getTsDate()
            });
            setModal(false);
            mutate();
            notify('Updated');
        } catch (err) {
            notify(err, 'error');
        }
    }

    const deactivateDisplay = async (deviceId: string) => {
        try {
            await apiClient.post(`/display-activation/deactivate`, {
                deviceId,
                user: user.s_email,
                date: getTsDate()
            });
            setModal(false);
            mutate();
            notify('Device Deactivated');
        } catch (err) {
            alert(err);
        }
    }

    const updateDisplays = async () => {
        try {
            await apiClient.post(`/display-activation/check-update`);
            setModal(false);
            mutate();
            notify('Update request sent');
        } catch (err) {
            alert(err);
        }
    }

    return (
        <div>
            <Button className={'mb-2'} onClick={() => updateDisplays()}>Update Displays</Button>
            <VirtualTable<DeviceActivations>
                data={data}
                mapping={[{
                    name: 'Device ID',
                    value: 'device_id',
                    customWidth: 300
                }, {
                    name: 'Name',
                    value: 'name',
                    customWidth: 200
                }, {
                    name: 'Unit',
                    value: 'unit',
                    customWidth: 100
                }, {
                    name: 'Status',
                    value: 'status',
                    customWidth: 100,
                    bgColorCondition: (item) => item.status === 'offline',
                    bgColor: 'yellow'
                }, {
                    name: 'Last seen',
                    value: 'last_seen',
                    datetime: true
                }, {
                    name: 'Disconnect Reason',
                    value: 'last_disconnect_reason'
                }, {
                    name: 'Modified',
                    value: 'updated_at',
                    datetime: true
                }, {
                    name: 'Modified by',
                    value: 'user_id',
                    customWidth: 300
                }]}
                handleClick={(item: DeviceActivations) => handleSelectItem(item.device_id)}
            />
            <ModalUpdate
                modal={modal}
                setModal={setModal}
                selectedItem={selectedItem}
                updateSettings={updateSettings}
                deactivateDisplay={deactivateDisplay}
            />
        </div>
    );
}